import type { Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { SheetGeometryCategory, SheetGeometryCategoryTable } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { componentInstanceChainFromId } from "doc/entity/componentInstanceEntityResolve";
import { docEntityHandleIsTableBased } from "doc/entity/docEntityHandleIsTableBased";
import type { Categorized3DGeometry, SheetGeometrySelector } from "doc/plans/planGeometrySourceTypes";
import { getReportableUnitDesignatorLabelIds } from "doc/utils/labelTools";
import { reportingUnitLabelFromEntityId } from "doc/utils/reportingUnitDocTools";
import { getDocEntityHandlesFromGeometry } from "./sheetGeometryHandleUtils";

/** True when a handle is scoped to a component instance (e.g. `room__ci__instance`). */
function handleBelongsToComponentInstance(handle: DocEntityHandle): boolean {
  if (!docEntityHandleIsTableBased(handle)) {
    return false;
  }
  return componentInstanceChainFromId(handle.id) !== undefined;
}

type GeometrySelector = SheetGeometrySelector;

export interface ReportingUnitSheetGeometrySelectorOptions {
  readonly doc: Doc;
  readonly selectedReportingUnitLabelId: string | undefined;
}

export interface RoomReportingUnitSheetGeometrySelectorsOptions {
  readonly doc: Doc;
  readonly selectedReportingUnitLabelId: string | undefined;
  readonly categories: readonly SheetGeometryCategory[];
}

export function makeReportingUnitSheetGeometrySelector({
  doc,
  selectedReportingUnitLabelId,
}: ReportingUnitSheetGeometrySelectorOptions): GeometrySelector | undefined {
  if (!selectedReportingUnitLabelId) {
    return undefined;
  }

  const reportableUnitLabelIds = getReportableUnitDesignatorLabelIds(doc);
  if (!reportableUnitLabelIds.includes(selectedReportingUnitLabelId)) {
    return undefined;
  }

  return (geometry: SheetGeometry | Categorized3DGeometry): boolean => {
    const handles = getDocEntityHandlesFromGeometry(geometry);
    if (handles.length === 0) {
      // No handles to attribute this geometry to a unit; keep it.
      return true;
    }

    // Keep geometry that resolves to the selected unit. Hide geometry that resolves to another unit
    // or is scoped to a non-selected component instance. Keep anything else (no unit, no instance).
    let belongsToOtherUnitOrInstance = false;
    for (const handle of handles) {
      const reportingUnitLabelId = reportingUnitLabelFromEntityId(doc, reportableUnitLabelIds, handle);
      if (reportingUnitLabelId === selectedReportingUnitLabelId) {
        return true;
      }
      if (reportingUnitLabelId !== undefined || handleBelongsToComponentInstance(handle)) {
        belongsToOtherUnitOrInstance = true;
      }
    }

    return !belongsToOtherUnitOrInstance;
  };
}

export function makeRoomReportingUnitSheetGeometrySelectors({
  doc,
  selectedReportingUnitLabelId,
  categories,
}: RoomReportingUnitSheetGeometrySelectorsOptions): SheetGeometryCategoryTable<GeometrySelector> {
  const roomReportingUnitSelector = makeReportingUnitSheetGeometrySelector({
    doc,
    selectedReportingUnitLabelId,
  });
  if (!roomReportingUnitSelector) {
    return {};
  }

  const reportingUnitSelectors: SheetGeometryCategoryTable<GeometrySelector> = {};
  for (const category of categories) {
    reportingUnitSelectors[category] = roomReportingUnitSelector;
  }

  return reportingUnitSelectors;
}
